import { inject, Injectable } from '@angular/core'
import { BehaviorSubject, combineLatest, from, map, Observable, of, shareReplay, Subject, switchMap } from 'rxjs'
import { ClientIPTreeData, ClientIPTreeNode, ClientIPTreeSimpleData } from '../../types/ClientIPListTree'
import { ClientIpListTreeNodesTransformService } from '../client-ip-list-tree-nodes-transform/client-ip-list-tree-nodes-transform.service'
import { ClientIPList } from '../../../../../../models/ClientIPList'
import { catchError, distinctUntilChanged, startWith, tap } from 'rxjs/operators'
import { ClientIpListTreeNodesAdjustService } from '../client-ip-list-tree-nodes-adjust/client-ip-list-tree-nodes-adjust.service'
import { ExpandState, ExpandStateType } from '../../types/ExpandState'
import { ClientIpListTreeNodesSettingsService } from '../client-ip-list-tree-nodes-settings/client-ip-list-tree-nodes-settings.service'
import { ClientIPOutsideSettings } from '../../types/ClientIPListSettings'
import { DateUtils } from 'src/app/services/date-utils.service'
import { ClientIpListSettingsService } from '../client-ip-list-settings/client-ip-list-settings.service'
import { ModalController } from '@ionic/angular'
import { IpJournalCloneEditorComponent } from '../../../client-ip-editors/ip-journal-clone-editor/ip-journal-clone-editor.component'
import { FeathersService } from '../../../../../../services/feathers.service'

@Injectable({
  providedIn: 'root',
})
export class ClientIpListService {
  private feathers = inject(FeathersService)
  private nodesTransformService = inject(ClientIpListTreeNodesTransformService)
  private nodesSettingService = inject(ClientIpListTreeNodesSettingsService)
  private nodesAdjustService = inject(ClientIpListTreeNodesAdjustService)
  private settingsService = inject(ClientIpListSettingsService)
  private modalCtrl = inject(ModalController)

  // Tree nodes
  private treeNodes$: Observable<ClientIPTreeNode[]>
  private filteredTreeNodes$: Observable<ClientIPTreeNode[]>

  // Expanded state
  private expandStateSubject$$ = new BehaviorSubject<ExpandStateType>(null)
  expandState$ = this.expandStateSubject$$.asObservable().pipe(distinctUntilChanged())

  // Loading
  private isLoadingSubject$$ = new BehaviorSubject<boolean>(false)

  // Reloading
  private reload$ = new Subject<void>()

  private filteredTreeNodesCache = new Map<number, Observable<ClientIPTreeNode[]>>()

  constructor() {}

  patchOutsideSettings(settings: Partial<ClientIPOutsideSettings>): void {
    this.settingsService.patchOutsideSettings(settings)
  }

  getFilteredTreeNodes(clientId: number): Observable<ClientIPTreeNode[]> {
    // Check if the treeNodes are already cached in the service
    // when the clientId is the same and the treeNodes are already loaded
    // Service is used in multiple components, so this is backup
    // to ensure that for a given clientId exists only one stream and one find()
    const cached = this.filteredTreeNodesCache.get(clientId)
    if (cached) {
      return cached
    }

    this.isLoadingSubject$$.next(true)

    this.treeNodes$ = this.getTreeNodes(clientId)

    this.filteredTreeNodes$ = combineLatest([
      this.treeNodes$,
      this.settingsService.getSettingsAsObservable(),
      this.expandState$,
    ]).pipe(
      switchMap(([nodes, settings, expandState]) => {
        // Use settings nodes
        let processedNodes = this.nodesSettingService.useSettingsOnTreeNodes(nodes, settings)

        // Apply to expand/collapse state
        if (expandState === ExpandState.EXPAND_ALL) {
          processedNodes = this.nodesAdjustService.expandAllNodes(processedNodes)
          this.resetExpandState()
        } else if (expandState === ExpandState.COLLAPSE_ALL) {
          processedNodes = this.nodesAdjustService.collapseAllNodes(processedNodes)
          this.resetExpandState()
        }

        return of(processedNodes)
      }),
      tap(() => this.isLoadingSubject$$.next(false)),
      catchError((error) => {
        this.isLoadingSubject$$.next(false)
        throw error
      }),
    )

    this.filteredTreeNodesCache.set(clientId, this.filteredTreeNodes$)
    return this.filteredTreeNodes$
  }

  getDataLoadingDetail(): Observable<boolean> {
    return this.isLoadingSubject$$.pipe(distinctUntilChanged())
  }

  expandAll() {
    this.expandStateSubject$$.next(ExpandState.EXPAND_ALL)
  }

  collapseAll() {
    this.expandStateSubject$$.next(ExpandState.COLLAPSE_ALL)
  }

  getClientIpTreeSimpleData(data: ClientIPTreeData): ClientIPTreeSimpleData {
    if (!data) {
      return undefined
    }
    return {
      id: data.id,
      programId: data.programId,
      type: data.type,
      programName: data.programName,
    }
  }

  areModalSettingsDefault() {
    return this.settingsService.emptySettings()
  }

  reload() {
    this.filteredTreeNodesCache.clear()
    this.reload$.next()
  }

  private getTreeNodes(clientId: number): Observable<ClientIPTreeNode[]> {
    return this.getClientIPList(clientId).pipe(
      map((clientIPList) => {
        this.initializeClientIpListDates(clientIPList)
        return this.nodesTransformService.transformToTreeNodes(clientIPList)
      }),
      shareReplay(1),
    )
  }

  private getClientIPList(clientId: number): Observable<ClientIPList[]> {
    return this.reload$.pipe(
      startWith(null),
      switchMap((_) =>
        from(
          this.feathers.service(ClientIPList.serviceName).find({
            query: {
              clientId,
            },
          }),
        ),
      ),
    )
  }

  private resetExpandState() {
    this.expandStateSubject$$.next(null)
  }

  private initializeClientIpListDates(clientIPList: ClientIPList[]) {
    clientIPList.map((singleClientIpListRecord) => {
      singleClientIpListRecord.programEnrollment?.map((programEnrollment) => {
        programEnrollment.archivedAt = DateUtils.initializeDate(programEnrollment.archivedAt)
        programEnrollment.dueDate = DateUtils.initializeDate(programEnrollment.dueDate)
        programEnrollment.subprogramEnrollments?.map((subprogramEnrollment) => {
          subprogramEnrollment.archivedAt = DateUtils.initializeDate(subprogramEnrollment.archivedAt)
          subprogramEnrollment.dueDate = DateUtils.initializeDate(subprogramEnrollment.dueDate)
          subprogramEnrollment.individualPlans?.map((individualPlan) => {
            individualPlan.closedAt = DateUtils.initializeDate(individualPlan.closedAt)
            individualPlan.dueDate = DateUtils.initializeDate(individualPlan.dueDate)
            individualPlan.goals?.map((goal) => {
              goal.completedAt = DateUtils.initializeDate(goal.completedAt)
              goal.dueDate = DateUtils.initializeDate(goal.dueDate)
              goal.steps?.map((step) => {
                step.completedAt = DateUtils.initializeDate(step.completedAt)
                step.dueDate = DateUtils.initializeDate(step.dueDate)
              })
            })
          })
        })
      })
    })
  }

  async showIPJournalCopyModal(clientId: number) {
    const modal = await this.modalCtrl.create({
      component: IpJournalCloneEditorComponent,
      componentProps: { clientId: clientId },
    })

    return await modal.present()
  }
}
